/**
 * Download = an export job (`POST /v1/exports`) polled until `ready`, then the
 * signed URL. Both download buttons ask for `mp4`, so what lands is ONE file
 * that plays; `clip_bundle` (a zip of separate clips) stays in the API for a
 * caller that wants the clips apart. The poll names the file
 * (`?file_name=`, see `downloadName`), so the link saves it under that name.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { dev, get, post } from "../api/client";
import type { ExportView } from "../api/types";
import type { ResolvedRoster } from "./roster";

/**
 * The saved file's name without its extension (owner, 2026-10-18): a player's
 * clip is `batsman3_clip2` or `bowler1_clip4`, the stage's label ("Batsman 3",
 * whatever the player was named) and the clip's place in that player's clips,
 * as the page shows "2 of 9". A match-up is `bowler1_batsman3_clip2`, the whole
 * match `match_clip7`; every clip on the page in one file is `batsman3_clips`,
 * `match_clips`. A player the roster does not list keeps the role alone.
 */
export function downloadName(r: Pick<ResolvedRoster, "batsmen" | "bowlers">, picked: { batsman: string | null; bowler: string | null }, clip: number | "all"): string {
  const part = (role: "batsman" | "bowler", id: string | null) => {
    if (!id) return null;
    const p = (role === "batsman" ? r.batsmen : r.bowlers).find((x) => x.id === id);
    return `${role}${p ? p.order : ""}`;
  };
  const who = [part("bowler", picked.bowler), part("batsman", picked.batsman)].filter(Boolean).join("_") || "match";
  return clip === "all" ? `${who}_clips` : `${who}_clip${clip}`;
}

export type ExportState = { phase: "idle" } | { phase: "requesting" } | { phase: "preparing"; id: string } | { phase: "ready"; id: string; url: string } | { phase: "failed"; message: string };

export function useExport(): { state: ExportState; start: (ids: string[], format: "mp4" | "clip_bundle", fileName?: string) => Promise<void>; reset: () => void } {
  const [state, setState] = useState<ExportState>({ phase: "idle" });
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const start = useCallback(async (ids: string[], format: "mp4" | "clip_bundle", fileName?: string) => {
    setState({ phase: "requesting" });
    try {
      const created = await post<{ export: { id: string } }>("/v1/exports", { delivery_ids: ids, format, quality: "working_copy" });
      const id = created.export.id;
      setState({ phase: "preparing", id });
      if (dev.enabled) await dev.finishExport(id).catch(() => undefined);
      const q = fileName ? `?file_name=${encodeURIComponent(fileName)}` : "";
      for (let i = 0; i < 60; i++) {
        const v = await get<ExportView>(`/v1/exports/${id}${q}`);
        if (v.export.state === "ready" && v.download_url) {
          if (alive.current) setState({ phase: "ready", id, url: v.download_url });
          return;
        }
        if (v.export.state === "failed") throw new Error("the export failed");
        await new Promise((r) => setTimeout(r, i < 5 ? 500 : 2000));
      }
      throw new Error("the export is taking longer than expected; try again later");
    } catch (e) {
      if (alive.current) setState({ phase: "failed", message: (e as Error).message });
    }
  }, []);
  return { state, start, reset: () => setState({ phase: "idle" }) };
}

export function exportLabel(s: ExportState, idle: string): string {
  switch (s.phase) {
    case "idle": return idle;
    case "requesting": return "Requesting…";
    case "preparing": return "Preparing…";
    case "ready": return "Download ready";
    case "failed": return "Try again";
  }
}
